/**
 * EmptyState — illustrative empty-state primitive (issue #429).
 *
 * Renders a 60-80px illustration (or supplied ReactNode), a
 * heading, optional body copy, and a primary CTA. Used by the
 * first-run SLP, parent, and admin surfaces to give a brand-new
 * user a deliberate "what do I do next?" path instead of a
 * blank navy canvas with a single line of text.
 *
 * The wrapper sets aria-live="polite" so screen readers announce
 * the empty state on mount (per acceptance criterion #8). When
 * the empty state appears in response to a destructive action,
 * pass `live="assertive"` to escalate the announcement.
 *
 * Ships 7 reusable inline SVG illustrations via the
 * `<StudentsIllustration/>`, `<MessagesIllustration/>`,
 * `<CalendarIllustration/>`, `<ListIllustration/>`,
 * `<InviteIllustration/>`, `<SessionsIllustration/>`, and
 * `<GoalsIllustration/>` named exports. The visual style is a
 * single line-art family — consistent stroke width, rounded
 * caps, and the brand mint palette so the empty states feel
 * like one product.
 */
import type { ReactElement, ReactNode } from 'react';

/**
 * Stroke color used by all the stock illustrations. Pulled from
 * the design tokens so future palette tweaks ripple automatically.
 */
const ILLUSTRATION_STROKE = 'var(--accent, #4caf50)';
const ILLUSTRATION_BG = 'var(--accent-soft, rgba(76, 175, 80, 0.18))';
const ILLUSTRATION_MUTED = 'var(--fg-tertiary, rgba(255, 255, 255, 0.4))';

export interface EmptyStateProps {
  illustration?: ReactNode;
  title: string;
  body?: string;
  cta?: { label: string; onClick: () => void };
  /** Default 'polite'; pass 'assertive' for destructive states. */
  live?: 'polite' | 'assertive';
}

export function EmptyState({
  illustration,
  title,
  body,
  cta,
  live = 'polite',
}: EmptyStateProps): ReactElement {
  return (
    <div className="empty-state" aria-live={live}>
      {illustration && (
        <div className="empty-state-illustration" aria-hidden="true">
          {illustration}
        </div>
      )}
      <h2 className="empty-state-title">{title}</h2>
      {body && <p className="empty-state-body">{body}</p>}
      {cta && (
        <button
          type="button"
          className="snap-button empty-state-cta"
          onClick={cta.onClick}
        >
          {cta.label}
        </button>
      )}
    </div>
  );
}

/**
 * StudentsIllustration — line-art 3 figures with speech bubbles.
 * Used by the Caseload empty state and the Practice tab on
 * StudentDetailScreen.
 */
export function StudentsIllustration(): ReactElement {
  return (
    <svg
      width="72"
      height="72"
      viewBox="0 0 72 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Three students"
    >
      <circle cx="36" cy="36" r="34" fill={ILLUSTRATION_BG} />
      {/* Left figure */}
      <circle cx="20" cy="28" r="5" stroke={ILLUSTRATION_STROKE} strokeWidth="2" />
      <path
        d="M11 50c0-5 4-9 9-9s9 4 9 9"
        stroke={ILLUSTRATION_STROKE}
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Center figure (taller) */}
      <circle cx="36" cy="24" r="6" stroke={ILLUSTRATION_STROKE} strokeWidth="2" />
      <path
        d="M25 52c0-6 5-11 11-11s11 5 11 11"
        stroke={ILLUSTRATION_STROKE}
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Right figure */}
      <circle cx="52" cy="28" r="5" stroke={ILLUSTRATION_STROKE} strokeWidth="2" />
      <path
        d="M43 50c0-5 4-9 9-9s9 4 9 9"
        stroke={ILLUSTRATION_STROKE}
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Speech bubble above center figure */}
      <path
        d="M30 12h12a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-5l-2 3-2-3h-3a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2z"
        fill="none"
        stroke={ILLUSTRATION_MUTED}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * MessagesIllustration — chat bubbles. Used by the parent
 * "no messages yet" empty state.
 */
export function MessagesIllustration(): ReactElement {
  return (
    <svg
      width="72"
      height="72"
      viewBox="0 0 72 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Chat messages"
    >
      <circle cx="36" cy="36" r="34" fill={ILLUSTRATION_BG} />
      {/* Outgoing bubble */}
      <path
        d="M16 22a4 4 0 0 1 4-4h20a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H26l-5 4v-4h-1a4 4 0 0 1-4-4V22z"
        fill="none"
        stroke={ILLUSTRATION_STROKE}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* Incoming bubble */}
      <path
        d="M28 40a4 4 0 0 1 4-4h20a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4h-2v4l-5-4h-13a4 4 0 0 1-4-4V40z"
        fill="none"
        stroke={ILLUSTRATION_STROKE}
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * CalendarIllustration — calendar page with a highlighted day.
 * Used by the OrgSettingsScreen "no scheduled sessions" state.
 */
export function CalendarIllustration(): ReactElement {
  return (
    <svg
      width="72"
      height="72"
      viewBox="0 0 72 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Calendar"
    >
      <circle cx="36" cy="36" r="34" fill={ILLUSTRATION_BG} />
      <rect
        x="16"
        y="20"
        width="40"
        height="34"
        rx="3"
        fill="none"
        stroke={ILLUSTRATION_STROKE}
        strokeWidth="2"
      />
      <path d="M16 28h40" stroke={ILLUSTRATION_STROKE} strokeWidth="2" />
      <path d="M24 16v8M48 16v8" stroke={ILLUSTRATION_STROKE} strokeWidth="2" strokeLinecap="round" />
      <circle cx="36" cy="40" r="4" fill={ILLUSTRATION_STROKE} />
    </svg>
  );
}

/**
 * ListIllustration — rows with bullet checkmarks. Used by the
 * AuditLogScreen "no entries yet" empty state.
 */
export function ListIllustration(): ReactElement {
  return (
    <svg
      width="72"
      height="72"
      viewBox="0 0 72 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="List of items"
    >
      <circle cx="36" cy="36" r="34" fill={ILLUSTRATION_BG} />
      {/* Row 1 */}
      <rect x="22" y="22" width="14" height="14" rx="2" fill="none" stroke={ILLUSTRATION_STROKE} strokeWidth="2" />
      <path d="M25 29l3 3 5-6" stroke={ILLUSTRATION_STROKE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M42 28h16" stroke={ILLUSTRATION_MUTED} strokeWidth="2" strokeLinecap="round" />
      <path d="M42 32h10" stroke={ILLUSTRATION_MUTED} strokeWidth="2" strokeLinecap="round" />
      {/* Row 2 */}
      <rect x="22" y="40" width="14" height="14" rx="2" fill="none" stroke={ILLUSTRATION_STROKE} strokeWidth="2" />
      <path d="M25 47l3 3 5-6" stroke={ILLUSTRATION_STROKE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M42 46h16" stroke={ILLUSTRATION_MUTED} strokeWidth="2" strokeLinecap="round" />
      <path d="M42 50h10" stroke={ILLUSTRATION_MUTED} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * InviteIllustration — paper plane / invite. Used by the
 * MemberRosterScreen "no members yet" empty state.
 */
export function InviteIllustration(): ReactElement {
  return (
    <svg
      width="72"
      height="72"
      viewBox="0 0 72 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Invite"
    >
      <circle cx="36" cy="36" r="34" fill={ILLUSTRATION_BG} />
      <path
        d="M16 36l40-18-14 40-6-16-20-6z"
        fill="none"
        stroke={ILLUSTRATION_STROKE}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <path d="M36 42l20-24" stroke={ILLUSTRATION_STROKE} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * SessionsIllustration — a clipboard with a checkmark. Used by the
 * student-detail Sessions empty state (issue #438 §13).
 */
export function SessionsIllustration(): ReactElement {
  return (
    <svg
      width="72"
      height="72"
      viewBox="0 0 72 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Sessions"
    >
      <circle cx="36" cy="36" r="34" fill={ILLUSTRATION_BG} />
      <rect x="20" y="18" width="32" height="40" rx="4" fill="none" stroke={ILLUSTRATION_STROKE} strokeWidth="2" />
      <path d="M28 14v8M44 14v8" stroke={ILLUSTRATION_STROKE} strokeWidth="2" strokeLinecap="round" />
      <path d="M26 28h20" stroke={ILLUSTRATION_MUTED} strokeWidth="2" strokeLinecap="round" />
      <path d="M26 36h14" stroke={ILLUSTRATION_MUTED} strokeWidth="2" strokeLinecap="round" />
      <path d="M30 46l4 4 8-8" stroke={ILLUSTRATION_STROKE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * GoalsIllustration — a target with a dart in the bullseye. Used by
 * the student-detail Goals empty state (issue #438 §13).
 */
export function GoalsIllustration(): ReactElement {
  return (
    <svg
      width="72"
      height="72"
      viewBox="0 0 72 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Goals"
    >
      <circle cx="36" cy="36" r="34" fill={ILLUSTRATION_BG} />
      <circle cx="36" cy="36" r="20" fill="none" stroke={ILLUSTRATION_STROKE} strokeWidth="2" />
      <circle cx="36" cy="36" r="12" fill="none" stroke={ILLUSTRATION_STROKE} strokeWidth="2" />
      <circle cx="36" cy="36" r="4" fill={ILLUSTRATION_STROKE} />
      <path d="M52 20l-3-3M22 22l-3-3" stroke={ILLUSTRATION_MUTED} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
